import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { api } from "../helpers/utils";
import { auth, provider, signInWithPopup, getRedirectResult } from "../helpers/FirebaseConfig";

const LS_USER_KEY = "ads_user";
const BANNER_MAX_HEIGHT = 160;

const AdsDashboard = () => {
  const [ads, setAds] = useState([]);
  const [user, setUser] = useState(null);
  const [, setLoading] = useState(false);
  const [isLoadingState, setIsLoadingState] = useState(false);
  const [toast, setToast] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deletingId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  /* ---------- TOAST ---------- */
  const showToast = useCallback((message, variant = "success") => {
    setToast({ message, variant });
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3200);
    return () => clearTimeout(t);
  }, [toast]);

  /* ---------- LOGOUT ---------- */
  const logout = useCallback(() => {
    localStorage.removeItem(LS_USER_KEY);
    setUser(null);
    setAds([]);
    navigate("/ads", { replace: true });
  }, [navigate]);

  const persistFirebaseUser = useCallback(
    (firebaseUser) => {
      if (!firebaseUser?.email) {
        showToast("Google login failed. Missing email.", "error");
        return;
      }

      const userData = {
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email.split("@")[0],
      };
      localStorage.setItem(LS_USER_KEY, JSON.stringify(userData));
      setUser(userData);
      setIsLoadingState(true);
    },
    [showToast]
  );

  /* ---------- HANDLE REDIRECT LOGIN FLOWS ---------- */
  useEffect(() => {
    let isMounted = true;

    const resolveRedirectLogin = async () => {
      try {
        const result = await getRedirectResult(auth);
        if (result?.user && isMounted) {
          persistFirebaseUser(result.user);
        }
      } catch (err) {
        console.error("Redirect login error:", err);
        showToast("Google login was interrupted. Please try again.", "error");
      }
    };

    resolveRedirectLogin();
    return () => {
      isMounted = false;
    };
  }, [persistFirebaseUser, showToast]);

  /* ---------- LOAD USER FROM LOCAL STORAGE ---------- */
  useEffect(() => {
    const storedUser = localStorage.getItem(LS_USER_KEY);
    if (storedUser && !user) {
      setUser(JSON.parse(storedUser));
      setIsLoadingState(true);
    }
  }, [user]);

  /* ---------- MANUAL GOOGLE LOGIN ---------- */
  const handleLogin = useCallback(async () => {
    try {
      let result

      // if (isMobileDevice) {
      //  result =  await signInWithRedirect(auth, provider);
      // }
      result = await signInWithPopup(auth, provider);

      const res = await api.post('/adsuser-login', { email: result.user.email })

      localStorage.setItem('authToken', res.data.token);
      //  console.log("Google login successful:", result);
      persistFirebaseUser(result.user);
    } catch (e) {
      console.error("Google login error:", e);
      showToast("Google login failed. Please try again.", "error");
    }
  }, [persistFirebaseUser, showToast]);

  const userEmail = user?.email;

  /* ---------- FETCH ADS ---------- */
  const fetchAds = useCallback(
    async (email) => {
      if (!email) return;
      setLoading(true);
      try {
        const res = await api.post("/my-promotions", { email });
        setAds(res.data?.data || []);
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          showToast("Session expired. Please login again.", "error");
          logout();
        } else {
          showToast("Could not load ads", "error");
        }
        setAds([]);
      } finally {
        setLoading(false);
      }
    },
    [logout, showToast]
  );

  useEffect(() => {
    if (userEmail) fetchAds(userEmail);
  }, [userEmail, fetchAds]);

  /* ---------- REFRESH AFTER CREATE ---------- */
  const refreshRequested = location.state?.refreshAds;

  useEffect(() => {
    if (!refreshRequested || !userEmail) return;
    fetchAds(userEmail).finally(() =>
      navigate(location.pathname, { replace: true })
    );
  }, [refreshRequested, userEmail, fetchAds, navigate, location.pathname]);

  /* ---------- CREATE ---------- */
  const handleCreate = () => {
    navigate(`/upload-ads?email=${userEmail}`)
  };

  /* ---------- DELETE ---------- */
  const requestDelete = (ad) => setPendingDelete(ad);
  const cancelDelete = () => setPendingDelete(null);

  const confirmDelete = useCallback(async (adId) => {


    try {

      const res = await api.post(`/delete-ad`, {

        adId: adId, email: userEmail

      });
      if(res.data.status === 100  )  {
        setAds((prev) => prev.filter((x) => x._id !== adId));
        setPendingDelete(null); 
      }

      else showToast("Failed to delete ad", "error");

    } catch (error) {

    }
  }, [showToast, userEmail]);

  const goToEdit = (id) => navigate(`/upload-ads?email=${userEmail}&adId=${id}`);

  const skeletonItems = useMemo(() => new Array(3).fill(null), []);

  /* ---------- LOGIN GATE ---------- */
  if (!user) {
    return (
      <Page>
        <Glow />
        <LoginGate>
          <Title>Pepchat Ads</Title>

             <PromoInfo>
            <PromoText>
              <PromoEyebrow>Run ads on Pepchat</PromoEyebrow>
              <PromoTitle>₹100 / week · 500 guaranteed views</PromoTitle>
              <PromoDesc>
                Promote your brand directly to the Pepchat community. Ads sit inside the app’s discovery flow and every
                campaign comes with a minimum of 500 impressions. Open Pepchat to see live placements.
              </PromoDesc>
            </PromoText>
      
          </PromoInfo>
          <Subtitle>Sign in with Google to manage campaigns</Subtitle>
          <LoginButton onClick={handleLogin}>
            Continue with Google
          </LoginButton>

       
        </LoginGate>
      </Page>
    );
  }

  /* ---------- MAIN UI ---------- */
  return (
    <Page>
      <Glow />
      <Content>
        <Header>
          <div>
            <Eyebrow>Pepchat Ads</Eyebrow>
            <Title>My Ad Campaigns</Title>
            <Subtitle>{user.email}</Subtitle>
          </div>

          <HeaderActions>
            <CreateButton onClick={handleCreate}>
              + Create
            </CreateButton>
            <LogoutButton onClick={logout}>
              Logout
            </LogoutButton>
          </HeaderActions>
        </Header>

        <PromoInfo>
          <PromoText>
            <PromoEyebrow>Pepchat ads</PromoEyebrow>
            <PromoTitle>₹100 per week · 500 guaranteed views</PromoTitle>
            <PromoDesc>
              Your creatives live inside the verified feed and group flows. Each booking includes at least 500
              impressions. Open Pepchat to see existing placements and understand how users discover ads.
            </PromoDesc>
          </PromoText>
          <PromoAction href="https://destinyapp.in/#download" target="_blank" rel="noreferrer">
            Open Pepchat
          </PromoAction>
        </PromoInfo>

        {!isLoadingState && (
          <Grid>
            {skeletonItems.map((_, i) => (
              <CardSkeleton key={i} />
            ))}
          </Grid>
        )}

        {isLoadingState && ads.length === 0 && (
          <EmptyState>
            <EmptyIcon>P</EmptyIcon>
            <EmptyTitle>No ads yet</EmptyTitle>
            <EmptySubtitle>Create your first Pepchat campaign to start showing up inside the product.</EmptySubtitle>
            <CreateButton onClick={handleCreate}>
              Create Ad
            </CreateButton>
          </EmptyState>
        )}

        {isLoadingState && ads.length > 0 && (
          <Grid>
            {ads.map((ad) => (
              <AdCard key={ad._id} data-deleting={deletingId === ad._id}>
                <BannerPreviewCard>
                  <BannerMedia>
                    {ad.image ? (
                      <BannerImage src={ad.image} alt={ad.title} />
                    ) : (
                      <BannerPlaceholder>No image uploaded</BannerPlaceholder>
                    )}
                    <AdBadge>Ad</AdBadge>
                  </BannerMedia>

                  <BannerContent>
                    <TextGroup>
                      <BannerTitle>{ad.title || "Untitled promotion"}</BannerTitle>
                      <BannerDesc>{ad.desc || "No description added yet."}</BannerDesc>
                    </TextGroup>
                    <BannerCTA type="button">Explore</BannerCTA>
                  </BannerContent>
                </BannerPreviewCard>

                <StatsRow>
                  <StatPill>
                    <StatIcon>V</StatIcon>
                    <div>
                      <StatLabel>Views</StatLabel>
                      <StatValue>{ad.totalViews ?? 0}</StatValue>
                    </div>
                  </StatPill>

                  <StatPill>
                    <StatIcon>C</StatIcon>
                    <div>
                      <StatLabel>Clicks</StatLabel>
                      <StatValue>{ad.totalClicks ?? 0}</StatValue>
                    </div>
                  </StatPill>
                </StatsRow>

                <ActionRow>
                  <ActionButton onClick={() => goToEdit(ad._id)}>
                    Edit
                  </ActionButton>
                  <ActionButton
                    data-variant="danger"
                    onClick={() => requestDelete(ad)}
                  >
                    Delete
                  </ActionButton>
                </ActionRow>
              </AdCard>
            ))}
          </Grid>
        )}

        {toast && (
          <Toast data-variant={toast.variant}>
            {toast.message}
          </Toast>
        )}

        {pendingDelete && (
          <ModalOverlay>
            <ModalCard>
              <ModalTitle>Delete this ad?</ModalTitle>
              <ModalDesc>This removes the campaign from your Pepchat ads dashboard.</ModalDesc>
              <ModalActions>
                <ModalButton onClick={cancelDelete}>
                  Cancel
                </ModalButton>
                <ModalButton
                  data-variant="danger"
                  onClick={()=> confirmDelete(pendingDelete._id)}
                >
                  Delete
                </ModalButton>
              </ModalActions>
            </ModalCard>
          </ModalOverlay>
        )}
      </Content>
    </Page>
  );

};




const Page = styled.div`
  min-height: 100vh;
  background: radial-gradient(circle at top, #1f1f2b 0%, #0e0e12 45%, #030305 100%);
  color: #f4f4ff;
  font-family: "Inter", "Space Grotesk", sans-serif;
  position: relative;
  padding: 32px 20px 100px;
`;

const Glow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: radial-gradient(
    640px circle at top right,
    rgba(108, 92, 231, 0.3),
    transparent 60%
  );
  opacity: 0.6;
  z-index: 0;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: center;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Eyebrow = styled.p`
  color: #a0a0ff;
  letter-spacing: 0.2em;
  font-size: 11px;
  text-transform: uppercase;
  margin-bottom: 6px;
`;

const Title = styled.h1`
  font-size: clamp(28px, 6vw, 42px);
  margin: 0;
`;

const Subtitle = styled.p`
  color: #7b7b9c;
  margin-top: 6px;
  font-size: 15px;
`;

const PromoInfo = styled.div`
  background: rgba(26, 26, 34, 0.85);
  border-radius: 22px;
  padding: 20px 24px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  gap: 18px;
  align-items: center;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const PromoText = styled.div`
  flex: 1;
  min-width: 220px;
`;

const PromoEyebrow = styled.p`
  margin: 0 0 6px;
  color: #8c8fff;
  text-transform: uppercase;
  letter-spacing: 0.3em;
  font-size: 11px;
`;

const PromoTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 20px;
`;

const PromoDesc = styled.p`
  margin: 0;
  color: #b3b5d3;
  line-height: 1.6;
  font-size: 14px;
`;

const PromoAction = styled.a`
  padding: 12px 20px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6c5ce7, #7c7cff);
  color: #fff;
  text-decoration: none;
  font-weight: 600;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 30px rgba(108, 92, 231, 0.3);
  }
`;

const LoginGate = styled.div`
  max-width: 420px;
  margin: 80px auto 0;
  padding: 36px;
  border-radius: 28px;
  background: rgba(26, 26, 34, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-align: center;
`;

const LoginButton = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 18px;
  font-weight: 600;
  background: linear-gradient(135deg, #6c5ce7, #7c7cff);
  color: #fff;
  letter-spacing: 0.03em;
  cursor: pointer;
  margin-top: 24px;
`;

const buttonHover = `
  transform: translateY(-1px) scale(1.01);
  box-shadow: 0 10px 30px rgba(108, 92, 231, 0.4);
`;

const CreateButton = styled.button`
  border: none;
  padding: 14px 26px;
  border-radius: 999px;
  font-weight: 600;
  letter-spacing: 0.02em;
  background: linear-gradient(135deg, #6c5ce7, #7c7cff);
  color: #fff;
  cursor: pointer;
  transition: 0.25s ease;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.5), transparent);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
  }

  &:hover {
    ${buttonHover}
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(1, minmax(0, 1fr));
  gap: 22px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const AdCard = styled.div`
  background: #1a1a22;
  border-radius: 24px;
  padding: 22px;
  border: 1px solid rgba(255, 255, 255, 0.04);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  transition: border-color 0.3s ease, transform 0.3s ease, opacity 0.3s ease;

  &[data-deleting="true"] {
    opacity: 0.6;
    pointer-events: none;
  }

  &:hover {
    border-color: rgba(124, 124, 255, 0.4);
    transform: translateY(-4px);
  }
`;

const BannerPreviewCard = styled.div`
  width: 100%;
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.05);
  background: #11111a;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.03);
  margin-bottom: 18px;
`;

const BannerMedia = styled.div`
  position: relative;
  width: 100%;
  height: ${BANNER_MAX_HEIGHT}px;
  max-height: ${BANNER_MAX_HEIGHT}px;
  overflow: hidden;
  background: #1f1f2b;
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BannerPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  padding: 0 20px;
  text-align: center;
  background: linear-gradient(120deg, #251c3a, #1a2344);
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 18px 20px;

  @media (max-width: 560px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BannerTitle = styled.h4`
  margin: 0;
  font-size: clamp(16px, 3vw, 20px);
  color: #fff;
`;

const BannerDesc = styled.p`
  margin: 0;
  font-size: clamp(13px, 2vw, 15px);
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.4;
`;

const BannerCTA = styled.button`
  padding: 8px 22px;
  border-radius: 999px;
  background: linear-gradient(135deg, #6c5ce7, #8f7cff);
  border: none;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  cursor: default;
  letter-spacing: 0.03em;
`;

const AdBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #f7f7ff;
  background: rgba(6, 6, 12, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 18px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const StatPill = styled.div`
  background: rgba(124, 124, 255, 0.08);
  border-radius: 18px;
  padding: 14px;
  display: flex;
  gap: 12px;
  align-items: center;
  border: 1px solid rgba(124, 124, 255, 0.15);
  font-size: 13px;
  letter-spacing: 0.01em;

  span {
    font-size: 18px;
  }
`;

const StatLabel = styled.div`
  color: #9ca0c7;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.3em;
`;

const StatValue = styled.div`
  margin-top: 4px;
  font-size: 18px;
  font-weight: 600;
  color: #fff;
`;

const ActionRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 520px) {
    flex-direction: column;
  }
`;

const ActionButton = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  &[data-variant="danger"] {
    color: #ff9db1;
    border-color: rgba(255, 157, 177, 0.4);

    &:hover {
      background: rgba(255, 157, 177, 0.12);
    }
  }
`;

const EmptyState = styled.div`
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 26px;
  padding: 80px 24px;
  text-align: center;
  background: rgba(26, 26, 34, 0.7);
  margin-top: 40px;
`;

const EmptyIcon = styled.div`
  width: 86px;
  height: 86px;
  border-radius: 50%;
  margin: 0 auto 18px;
  background: linear-gradient(135deg, rgba(108, 92, 231, 0.4), rgba(124, 124, 255, 0.2));
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
`;

const EmptyTitle = styled.h3`
  font-size: 24px;
  margin-bottom: 8px;
`;

const EmptySubtitle = styled.p`
  color: #a6a8c7;
  max-width: 420px;
  margin: 0 auto 26px;
  line-height: 1.6;
`;

const CardSkeleton = styled.div`
  background: rgba(26, 26, 34, 0.6);
  border-radius: 24px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.04);
`;

const Toast = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 14px 18px;
  border-radius: 16px;
  background: rgba(26, 26, 34, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #fff;
  box-shadow: 0 15px 35px rgba(0, 0, 0, 0.5);
  font-size: 14px;
  z-index: 20;

  &[data-variant="error"] {
    border-color: rgba(255, 141, 159, 0.4);
    color: #ff9db1;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(5, 5, 8, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(4px);
  z-index: 30;
`;

const ModalCard = styled.div`
  background: #1a1a22;
  padding: 28px;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  max-width: 360px;
  width: calc(100% - 40px);
`;

const ModalTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 8px;
`;

const ModalDesc = styled.p`
  color: #a6a8c7;
  font-size: 14px;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  margin-top: 24px;
  display: flex;
  gap: 12px;

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ModalButton = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  transition: 0.2s ease;

  &[data-variant="danger"] {
    color: #ff9db1;
    border-color: rgba(255, 157, 177, 0.5);
    background: rgba(255, 157, 177, 0.12);
  }
`;

const StatIcon = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  background: rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 12px;
`;


const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LogoutButton = styled.button`
  background: rgba(255, 255, 255, 0.06);
  color: #ff6b6b;
  border: 1px solid rgba(255, 107, 107, 0.35);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 107, 107, 0.12);
  }

  &:active {
    transform: scale(0.97);
  }
`;


export default AdsDashboard;
